<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { flip } from 'svelte/animate';
  import { fade, fly, scale } from 'svelte/transition';
  import { Game } from '../game/Game';
  import { click } from '../game/Sfx';
  import { hud, togglePanel as toggle } from '../game/hud.svelte';
  import { app, leaveMatch, type MatchInfo } from '../lib/net.svelte';
  import { keyLabel, placeSpell, settings } from '../lib/settings.svelte';
  import { advance } from '../lib/tutorial';
  import Achievements from './Achievements.svelte';
  import Character from './Character.svelte';
  import Chat from './Chat.svelte';
  import ClassPicker from './ClassPicker.svelte';
  import Leaderboard from './Leaderboard.svelte';
  import Lobby from './Lobby.svelte';
  import Settings from './Settings.svelte';
  import Spellbook from './Spellbook.svelte';
  import Talents from './Talents.svelte';
  import ActionBar from './hud/ActionBar.svelte';
  import Scoreboard from './hud/Scoreboard.svelte';
  import AuraTooltip from './hud/AuraTooltip.svelte';
  import LossOfControl from './hud/LossOfControl.svelte';
  import Meter from './hud/Meter.svelte';
  import Movable from './hud/Movable.svelte';
  import SpellTooltip from './hud/SpellTooltip.svelte';
  import UnitFrame from './hud/UnitFrame.svelte';
  import { CLASS_SLOTS, type AbilityId, type AuraId } from '@arena/shared';

  let { match }: { match: MatchInfo } = $props();
  let hovered: AbilityId | null = $state(null);
  let hoveredAura: { unitId: string; auraId: AuraId } | null = $state(null);
  const hoverAura = (aura: typeof hoveredAura) => (hoveredAura = aura);
  const auraView = $derived.by(() => {
    if (!hoveredAura) return undefined;
    const { unitId, auraId } = hoveredAura;
    return [hud.player, hud.target, ...hud.team, ...hud.enemies]
      .find((f) => f?.id === unitId)
      ?.auras.find((a) => a.auraId === auraId);
  });
  // A removed icon never fires mouseleave.
  $effect(() => {
    if (hoveredAura && !auraView) hoveredAura = null;
  });
  let canvas: HTMLCanvasElement;
  let overlay: HTMLDivElement;
  let game: Game | undefined = $state();
  let errors: { id: number; text: string }[] = $state([]);
  let nextErrorId = 0;

  onMount(() => {
    game = new Game(canvas, overlay, match);
    void game.start();
    return () => game?.dispose();
  });

  $effect(() => {
    const error = hud.error;
    if (!error) return;
    const id = nextErrorId++;
    untrack(() => (errors = [...errors.slice(-2), { id, text: error.text }]));
    setTimeout(() => (errors = errors.filter((e) => e.id !== id)), 2500);
  });

  const clock = (s: number) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const target = (id: string) => game?.setTarget(id);
  const hub = $derived(match.bracket === 'hub');
  let lobby: Lobby | undefined = $state();
  $effect(() => {
    settings.tutorial = advance(settings.tutorial, {
      classes: settings.classes,
      panel: hud.panel,
      bracket: match.bracket,
      queued: app.queue.bracket !== null,
    });
  });
  /** Everyone else in the hub shares a team, so there the party is who's in ours. */
  const party = $derived(
    hub
      ? hud.team.filter((f) => app.party?.members.some((m) => m.id === f.id))
      : hud.team,
  );
  const cancelAura = (id: AuraId) => game?.cancelAura(id);
  let frameMenu = $state<{ x: number; y: number } | null>(null);
  let frameMenuEl: HTMLDivElement | undefined = $state();
  const openFrameMenu = (e: MouseEvent) => {
    click();
    frameMenu = { x: e.clientX, y: e.clientY };
  };
</script>

{#snippet tip(label: string, key: string | undefined)}
  <span class="tip" aria-hidden="true"
    >{label}{#if key}<kbd>{key}</kbd>{/if}</span
  >
{/snippet}

<svelte:window
  onpointerdown={(e) => {
    if (frameMenu && !frameMenuEl?.contains(e.target as Node)) frameMenu = null;
  }}
  onkeydowncapture={(e) => {
    if (e.key !== 'Escape' || !frameMenu) return;
    frameMenu = null;
    click();
    e.stopPropagation();
  }}
/>

<div
  class="game"
  class:hub={match.bracket === 'hub'}
  class:ghost={hud.player?.ghost}
>
  <canvas bind:this={canvas}></canvas>
  <div class="overlay" bind:this={overlay}></div>

  {#if !hud.ready}
    <div class="loading">
      <span>Entering the arena…</span>
      <div
        class="progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={hud.loading.total}
        aria-valuenow={hud.loading.done}
      >
        <div
          style:width="{(hud.loading.done / Math.max(1, hud.loading.total)) *
            100}%"
        ></div>
      </div>
    </div>
  {:else}
    <div class="ui" style:zoom={settings.uiScale}>
      <Movable
        id="cast"
        label="Cast bar"
        initial={{ ax: 0.5, ay: 1, x: 0, y: -184 }}
        onmenu={openFrameMenu}
      >
        {#if hud.player?.cast}
          <div class="cast" class:channel={hud.player.cast.channel}>
            <div
              class="fill"
              style="width: {hud.player.cast.progress * 100}%"
            ></div>
            <span class="cast-name">{hud.player.cast.name}</span>
            <span class="cast-time">{hud.player.cast.remaining.toFixed(1)}</span
            >
          </div>
        {/if}
      </Movable>
      <Movable
        id="swing"
        label="Swing timer"
        initial={{ ax: 0.5, ay: 1, x: 0, y: -226 }}
        onmenu={openFrameMenu}
      >
        {#if settings.swingTimer && hud.swing}
          <div class="swing" class:done={hud.swing.remaining <= 0}>
            <div class="fill" style="width: {hud.swing.progress * 100}%"></div>
            <span>{hud.swing.remaining.toFixed(1)}</span>
          </div>
        {/if}
      </Movable>
      <div class="bottom">
        {#if hud.showCombo}
          <div class="combo">
            {#each [1, 2, 3, 4, 5] as i (i)}<span class:on={hud.combo >= i}
              ></span>{/each}
          </div>
        {/if}
        {#if game}
          <div class="bars">
            {#if hud.swap}<ActionBar
                actions={[hud.swap]}
                size="medium"
                locked
                names={settings.spellNames}
                oncast={(id) => game?.cast(id)}
                onhover={(id) => (hovered = id)}
                onplace={() => {}}
              />{/if}
            <div class="rows">
              {#if hud.offActions.length}<ActionBar
                  actions={hud.offActions}
                  size="small"
                  locked={settings.lockBars}
                  names={false}
                  onhover={(id) => (hovered = id)}
                  onplace={(id, slot) =>
                    hud.player?.other && placeSpell(hud.player.other, id, slot)}
                />{/if}
              <ActionBar
                actions={hud.actions}
                locked={settings.lockBars}
                names={settings.spellNames}
                oncast={(id) => game?.cast(id)}
                onhover={(id) => (hovered = id)}
                onplace={(id, slot) =>
                  hud.player &&
                  (slot < CLASS_SLOTS
                    ? placeSpell(hud.player.classId, id, slot)
                    : placeSpell('utility', id, slot - CLASS_SLOTS))}
              />
            </div>
          </div>
        {/if}
      </div>

      {#if hovered && hud.player}
        <div class="spell-tooltip">
          <SpellTooltip
            abilityId={hovered}
            active={hud.actions.find((a) => a.abilityId === hovered)?.active}
          />
        </div>
      {:else if auraView}
        <div class="spell-tooltip"><AuraTooltip aura={auraView} /></div>
      {/if}

      <Movable
        id="player"
        label="Player"
        initial={{ ax: 0, ay: 0, x: 12, y: 12 }}
        onmenu={openFrameMenu}
      >
        {#if hud.player}<UnitFrame
            frame={hud.player}
            onmenu={openFrameMenu}
            onhover={hoverAura}
            oncancel={cancelAura}
          />{/if}
      </Movable>
      <Movable
        id="target"
        label="Target"
        initial={{ ax: 0, ay: 0, x: 304, y: 12 }}
        onmenu={openFrameMenu}
      >
        {#if hud.target}
          <div class="with-tot">
            <UnitFrame
              frame={hud.target}
              selected
              onmenu={hub ? (e) => lobby?.targetMenu(e) : undefined}
              onhover={hoverAura}
              oncancel={cancelAura}
            />
            <!-- Hub units have no target. Elsewhere it keeps its width, so a frame anchored right or center doesn't shift as it comes and goes. -->
            {#if !hub && settings.targetOfTarget}
              <div class="tot">
                {#if hud.targetTarget}
                  {@const tot = hud.targetTarget}
                  <UnitFrame frame={tot} mini onselect={() => target(tot.id)} />
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      </Movable>
      <Movable
        id="party"
        label="Party"
        initial={{ ax: 0, ay: 0, x: 12, y: 150 }}
        onmenu={openFrameMenu}
      >
        <div class="stack">
          {#each party as frame (frame.id)}
            <UnitFrame
              {frame}
              compact
              selected={hud.target?.id === frame.id}
              onselect={() => target(frame.id)}
              onmenu={hub ? (e) => lobby?.openMenu(frame.name, e) : undefined}
              onhover={hoverAura}
            />
          {/each}
        </div>
      </Movable>
      {#if !hub || !settings.lockFrames}
        <Movable
          id="enemies"
          label="Arena enemies"
          initial={{ ax: 1, ay: 0, x: -12, y: 120 }}
          onmenu={openFrameMenu}
        >
          <div class="stack right">
            {#each hud.enemies as frame (frame.id)}
              <UnitFrame
                {frame}
                compact
                mirrored
                selected={hud.target?.id === frame.id}
                onselect={frame.hidden ? undefined : () => target(frame.id)}
                onhover={hoverAura}
              />
            {/each}
          </div>
        </Movable>
      {/if}

      <div class="errors" class:hidden={!settings.errors}>
        {#each errors as error (error.id)}
          <div
            class="error"
            animate:flip={{ duration: 250 }}
            in:fade={{ duration: 150 }}
            out:fly={{ y: -24, duration: 400 }}
          >
            {error.text}
          </div>
        {/each}
      </div>

      {#if hub}
        <Lobby bind:this={lobby} />
      {:else}
        <div class="hud">
          <div class="top">
            {#if match.bracket === 'practice'}
              <span
                >Practice — dummies get back up, and your party can join from
                the practice master</span
              >
            {:else if hud.phase === 'prep'}
              <span class="prep"
                >Gates open in {Math.ceil(hud.phaseRemaining)}</span
              >
            {:else}
              <span>{match.bracket} · {clock(hud.phaseRemaining)}</span>
              {#if hud.dampening > 0}<span class="damp"
                  >Dampening {Math.round(hud.dampening * 100)}%</span
                >{/if}
            {/if}
            <span class="ping"
              >{app.ping ?? '–'} ms{#if settings.showFps}{` · ${hud.fps} fps`}{/if}</span
            >
          </div>

          {#if settings.lossOfControl && hud.lossOfControl}
            {@const loss = hud.lossOfControl}
            {#if loss.full}<div
                class="vignette"
                transition:fade|global={{ duration: 200 }}
              ></div>{/if}
            {#key `${loss.kind}:${loss.name}`}
              <div
                class="loss-of-control"
                in:scale|global={{ start: 1.4, duration: 180 }}
              >
                <LossOfControl {loss} />
              </div>
            {/key}
          {/if}

          <Chat channels={['party', 'yell']} />

          {#if settings.meter}
            <Movable
              id="meter"
              label="Damage meter"
              free
              initial={{ ax: 1, ay: 1, x: -12, y: -68 }}
              onmenu={openFrameMenu}
            >
              <Meter
                {...hud.meter}
                onreset={match.bracket === 'practice'
                  ? () => game?.resetMeter()
                  : undefined}
              />
            </Movable>
          {/if}

          <div class="corner">
            <button onclick={leaveMatch}
              >{match.bracket === 'practice' ? 'Leave' : 'Leave match'}</button
            >
          </div>

          {#if hud.player?.dead && !hud.player.ghost && match.bracket !== 'practice' && !app.result}
            <button class="release" onclick={() => game?.release()}
              >Release Spirit</button
            >
          {/if}

          {#if app.result}
            <Scoreboard
              winner={app.result.winner}
              units={app.result.units}
              youId={match.youId}
              onleave={leaveMatch}
            />
          {/if}
        </div>
      {/if}
      <div class="menus">
        <button
          class:active={hud.settingsOpen}
          aria-label="Settings"
          aria-pressed={hud.settingsOpen}
          onclick={() => (
            (hud.settingsOpen = !hud.settingsOpen),
            (hud.panel = null)
          )}
        >
          {@render tip('Settings', 'Esc')}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
          </svg>
        </button>
        {#if hub}
          <button
            class:active={hud.panel === 'leaderboard'}
            aria-label="Leaderboard"
            aria-pressed={hud.panel === 'leaderboard'}
            onclick={() => toggle('leaderboard')}
          >
            {@render tip('Leaderboard', keyLabel(settings.keys.leaderboard[0]))}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
              <path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" />
            </svg>
          </button>
          <button
            class:active={hud.panel === 'achievements'}
            aria-label="Achievements"
            aria-pressed={hud.panel === 'achievements'}
            onclick={() => toggle('achievements')}
          >
            {@render tip(
              'Achievements',
              keyLabel(settings.keys.achievements[0]),
            )}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="6" />
              <path d="M8.5 13 7 22l5-3 5 3-1.5-9" />
            </svg>
          </button>
          <button
            class:active={hud.panel === 'character'}
            aria-label="Character"
            aria-pressed={hud.panel === 'character'}
            onclick={() => toggle('character')}
          >
            {@render tip('Character', keyLabel(settings.keys.character[0]))}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="7" r="4" />
              <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
          </button>
        {/if}
      </div>
      <div class="menus right">
        {#if hub}
          <button
            class="class"
            class:active={hud.panel === 'classes'}
            aria-label="Class"
            aria-pressed={hud.panel === 'classes'}
            onclick={() => toggle('classes')}
          >
            {@render tip('Class', keyLabel(settings.keys.classes[0]))}
            <img src="/icons/{app.classes[0]}.webp" alt="" />
          </button>
        {/if}
        <button
          class:active={hud.panel === 'spellbook'}
          class:quest={settings.tutorial === 'spells'}
          aria-label="Spellbook"
          aria-pressed={hud.panel === 'spellbook'}
          onclick={() => toggle('spellbook')}
        >
          {@render tip('Spellbook', keyLabel(settings.keys.spellbook[0]))}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
            <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
          </svg>
        </button>
        <button
          class:active={hud.panel === 'talents'}
          aria-label="Talents"
          aria-pressed={hud.panel === 'talents'}
          onclick={() => toggle('talents')}
        >
          {@render tip('Talents', keyLabel(settings.keys.talents[0]))}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="4" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="12" cy="19" r="2" />
            <circle cx="19" cy="19" r="2" />
            <path d="M12 6v11M12 10c-4 0-7 3-7 7M12 10c4 0 7 3 7 7" />
          </svg>
        </button>
      </div>
      {#if hud.settingsOpen}<Settings
          onclose={() => (hud.settingsOpen = false)}
        />{/if}
      {#if hud.panel === 'talents'}<Talents
          locked={!hub && match.bracket !== 'practice'}
          onclose={() => (hud.panel = null)}
        />{/if}
      {#if hud.panel === 'spellbook' && hud.player}<Spellbook
          locked={!hub && match.bracket !== 'practice'}
          onhover={(id) => (hovered = id)}
          onclose={() => (hud.panel = null)}
        />{/if}
      {#if hub}
        {#if hud.panel === 'classes'}<ClassPicker
            onclose={() => (hud.panel = null)}
          />{/if}
        {#if hud.panel === 'leaderboard'}<Leaderboard
            onclose={() => (hud.panel = null)}
          />{/if}
        {#if hud.panel === 'achievements'}<Achievements
            onclose={() => (hud.panel = null)}
          />{/if}
        {#if hud.panel === 'character'}<Character
            onclose={() => (hud.panel = null)}
          />{/if}
      {/if}
      {#if frameMenu}
        <div
          class="panel menu"
          bind:this={frameMenuEl}
          style:left="{frameMenu.x / settings.uiScale}px"
          style:top="{frameMenu.y / settings.uiScale}px"
          style:transform={frameMenu.y > innerHeight / 2
            ? 'translateY(-100%)'
            : null}
        >
          <button
            onclick={() => (
              (settings.lockFrames = !settings.lockFrames),
              (frameMenu = null)
            )}>{settings.lockFrames ? 'Unlock frames' : 'Lock frames'}</button
          >
          <button onclick={() => ((settings.frames = {}), (frameMenu = null))}
            >Reset positions</button
          >
          <button onclick={() => (frameMenu = null)}>Cancel</button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .game {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
  .ghost canvas {
    filter: grayscale(1) brightness(0.85);
  }
  .release {
    position: absolute;
    top: 28%;
    left: 50%;
    transform: translateX(-50%);
    font-size: 18px;
    pointer-events: auto;
  }
  .overlay,
  .ui,
  .hud {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .loading {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 14px;
    font-size: 24px;
    color: var(--gold);
    background: var(--bg);
  }
  .progress {
    width: 260px;
    height: 6px;
    border: 1px solid var(--gold);
    border-radius: 3px;
    overflow: hidden;
  }
  .progress > div {
    height: 100%;
    background: var(--gold);
    transition: width 0.15s;
  }
  .top {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 16px;
    font-size: 18px;
    text-shadow: 0 1px 3px black;
  }
  .prep {
    color: var(--gold);
    font-size: 26px;
  }
  .damp {
    color: #ff8a5a;
  }
  .ping {
    align-self: center;
    font-size: 14px;
    color: var(--muted);
  }
  .stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .stack.right {
    align-items: flex-end;
  }
  .with-tot {
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }
  .tot {
    width: 160px;
    margin-top: 20px;
  }
  .menu {
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 140px;
    padding: 8px;
    pointer-events: auto;
    background: var(--panel-solid);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
  }
  .errors {
    position: absolute;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  .errors.hidden {
    display: none;
  }
  .loss-of-control {
    position: absolute;
    top: 34%;
    left: 50%;
    transform: translateX(-50%);
  }
  .vignette {
    position: absolute;
    inset: 0;
    box-shadow: inset 0 0 140px rgba(220, 30, 15, 0.55);
    animation: vignette-pulse 1s ease-in-out infinite alternate;
  }
  @keyframes vignette-pulse {
    from {
      opacity: 0.6;
    }
    to {
      opacity: 1;
    }
  }
  .error {
    color: #ff3a2a;
    font-size: 24px;
    font-weight: bold;
    white-space: nowrap;
    text-shadow:
      0 0 3px black,
      0 1px 3px black;
  }
  .bottom {
    position: absolute;
    bottom: 14px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .cast {
    position: relative;
    width: 400px;
    height: 30px;
    background: #0a0a0a;
    border: 2px solid var(--gold);
    border-radius: 4px;
    overflow: hidden;
  }
  .cast .fill {
    height: 100%;
    background: linear-gradient(#f3c650, #b98a1c);
    transition: width 0.05s linear;
  }
  .cast.channel .fill {
    background: linear-gradient(#7fd06a, #3f8a2a);
  }
  .cast span {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    font-size: 17px;
    font-weight: bold;
    text-shadow:
      0 0 3px black,
      0 1px 2px black;
  }
  .cast-name {
    left: 0;
    right: 0;
    justify-content: center;
  }
  .cast-time {
    right: 10px;
    font-size: 15px;
  }
  .swing {
    position: relative;
    width: 240px;
    height: 12px;
    background: #0a0a0a;
    border: 1px solid #8a8a8a;
    border-radius: 3px;
    overflow: hidden;
  }
  .swing.done {
    opacity: 0;
    transition: opacity 0.4s;
  }
  .swing .fill {
    height: 100%;
    background: linear-gradient(#e8e8e8, #8f8f8f);
  }
  .swing span {
    position: absolute;
    top: 0;
    right: 6px;
    bottom: 0;
    display: flex;
    align-items: center;
    font-size: 10px;
    font-weight: bold;
    text-shadow: 0 0 2px black;
  }
  .combo {
    display: flex;
    gap: 6px;
  }
  /* The swap button centres on the main row, which is 84px to its 63. */
  .bars {
    display: flex;
    align-items: flex-end;
    gap: 12px;
  }
  .bars > :global(.bar.medium) {
    margin-bottom: 10.5px;
  }
  .rows {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  /* As wide as the main bar's six class slots (84px, 6px apart), so the other class's row centres over them. */
  .rows > :global(.bar.small) {
    width: calc(6 * 84px + 5 * 6px);
    justify-content: center;
  }
  .combo span {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid #a83a2a;
    background: #2a0a08;
  }
  .combo span.on {
    background: radial-gradient(#ff6a4a, #b8241a);
  }
  .spell-tooltip {
    position: absolute;
    z-index: 1;
    bottom: 112px;
    right: 12px;
  }
  .corner {
    position: absolute;
    top: 10px;
    right: 12px;
    display: flex;
    gap: 8px;
    pointer-events: auto;
  }

  .menus {
    position: absolute;
    bottom: 14px;
    left: 12px;
    display: flex;
    gap: 8px;
  }
  .menus.right {
    left: auto;
    right: 12px;
  }
  .menus button {
    position: relative;
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    padding: 0;
    color: var(--muted);
    border-color: var(--border);
    pointer-events: auto;
  }
  .menus svg {
    width: 24px;
    height: 24px;
  }
  .menus img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 3px;
  }
  .tip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px;
    font-size: 14px;
    white-space: nowrap;
    color: #fff;
    background: var(--panel-solid);
    border: 1px solid var(--border);
    border-radius: 6px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    pointer-events: none;
    opacity: 0;
    transform: translateY(4px);
    transition:
      opacity 0.12s,
      transform 0.12s;
  }
  .menus.right .tip {
    left: auto;
    right: 0;
  }
  .menus button:hover .tip,
  .menus button:focus-visible .tip {
    opacity: 1;
    transform: none;
    transition-delay: 0.15s;
  }
  .tip kbd {
    padding: 1px 6px;
    font: inherit;
    font-size: 12px;
    font-weight: bold;
    color: #fff3d6;
    background: linear-gradient(#3b3428, #221d15);
    border: 1px solid #6b5a35;
    border-bottom-width: 3px;
    border-radius: 4px;
  }
  @media (prefers-reduced-motion: reduce) {
    .tip {
      transform: none;
    }
  }
  .menus .active,
  .menus .quest {
    color: var(--gold);
    border-color: var(--gold);
    box-shadow: 0 0 10px rgba(230, 192, 106, 0.6);
  }
  .menus .quest {
    animation: quest-pulse 1s ease-in-out infinite alternate;
  }
  @keyframes quest-pulse {
    to {
      box-shadow: 0 0 22px rgba(255, 211, 74, 0.95);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .menus .quest {
      animation: none;
    }
  }

  :global(.nameplate) {
    position: absolute;
    left: 0;
    top: 0;
    width: 140px;
    text-align: center;
    pointer-events: none;
    will-change: transform;
  }
  :global(.np-name) {
    font-size: 14px;
    font-weight: bold;
    text-shadow: 0 1px 2px black;
  }
  :global(.np-tag) {
    font-size: 12px;
    color: #d8d2c4;
    text-shadow: 0 1px 2px black;
  }
  :global(.nameplate.npc .np-name) {
    color: #ffd34a !important;
  }
  :global(.nameplate.quest::before) {
    content: '!';
    display: block;
    font-size: 36px;
    font-weight: bold;
    line-height: 1;
    color: #ffd34a;
    text-shadow:
      0 0 3px black,
      0 0 10px rgba(255, 180, 40, 0.9);
  }
  :global(.nameplate.critter .np-name) {
    color: #b4b4b4;
  }
  .hub :global(.np-hp) {
    display: none;
  }
  :global(.np-hp),
  :global(.np-cast) {
    height: 8px;
    margin-top: 2px;
    background: #111;
    border: 1px solid #000;
  }
  :global(.np-hp div) {
    height: 100%;
    background: #3fbf3f;
  }
  :global(.nameplate.enemy .np-hp div) {
    background: #d8402f;
  }
  :global(.np-cast) {
    visibility: hidden;
  }
  :global(.nameplate.targeted) {
    z-index: 1;
  }
  :global(.nameplate.targeted .np-hp) {
    height: 11px;
    border-color: #ffd34a;
    box-shadow: 0 0 6px rgba(255, 211, 74, 0.8);
  }
  :global(.nameplate:not(.targeted) .np-name) {
    opacity: 0.8;
  }
  :global(.np-debuffs) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 3px;
    margin-bottom: 3px;
  }
  :global(.np-debuff) {
    position: relative;
    width: 30px;
    height: 30px;
    border: 1px solid var(--enemy);
    border-radius: 2px;
    background: #2a1414;
    overflow: hidden;
  }
  :global(.np-debuff img) {
    display: block;
    width: 100%;
    height: 100%;
  }
  :global(.np-debuff span) {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    font-size: 12px;
    font-weight: bold;
    line-height: 12px;
    color: #ffd34a;
    text-shadow:
      0 0 2px black,
      0 0 2px black;
  }
  :global(.np-cast div) {
    height: 100%;
    background: #f3c650;
  }
  :global(.bubble) {
    position: absolute;
    left: 0;
    top: 0;
    max-width: 220px;
    padding: 5px 10px;
    font-size: 13px;
    text-align: center;
    overflow-wrap: anywhere;
    color: #fff;
    background: rgba(10, 11, 16, 0.85);
    border: 1px solid #6b5a35;
    border-radius: 10px;
    pointer-events: none;
    will-change: transform;
  }
  :global(.bubble::after) {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border: 6px solid transparent;
    border-top-color: #6b5a35;
  }
  :global(.bubble.yell) {
    color: #ff4040;
    font-weight: bold;
  }
  :global(.bubble.party) {
    color: #aaaaff;
  }
  :global(.combat-text) {
    position: absolute;
    transform: translate(-50%, -50%);
    font-weight: bold;
    font-size: 29px;
    text-shadow:
      0 0 3px black,
      0 1px 2px black;
    pointer-events: none;
    animation: float-up 1.3s ease-out forwards;
  }
  :global(.combat-text.melee) {
    color: #ffffff;
  }
  :global(.combat-text.spell) {
    color: #ffe04a;
  }
  :global(.combat-text.taken) {
    color: #ff4a3a;
    font-size: 23px;
  }
  :global(.combat-text.heal) {
    color: #5aff5a;
  }
  :global(.combat-text.info) {
    color: #d8b4ff;
    font-size: 21px;
  }
  :global(.combat-text.absorb) {
    color: #e8e8e8;
    font-size: 21px;
  }
  :global(.combat-text.kill) {
    color: #ff8a2a;
    font-size: 34px;
  }
  :global(.combat-text.periodic) {
    font-size: 20px;
  }
  @keyframes -global-float-up {
    from {
      opacity: 1;
      margin-top: 0;
    }
    to {
      opacity: 0;
      margin-top: -70px;
    }
  }
</style>
